import dayjs, { Dayjs } from "dayjs";

import type { PropertyHolder } from "@/view_models/atoms";

import { getLeaseContractForDate, getMonthlyRentByProperty, usesRoomIncome } from "./calculateProperty";
import { getMonthlyCashflowResultEffectiveOn } from "./propertyEdit";

// 満室想定の収入。日割りや空室シミュレーションを適用しない。
// 一棟は部屋情報を正とし、区分でも契約がなく部屋情報だけを持つ既存データに対応する。
export const getMonthlyIncomeForYield = (property: PropertyHolder, asOf: Dayjs = dayjs()): number => {
  const rent = getMonthlyRentByProperty(property, asOf);
  if (usesRoomIncome(property)) return rent;
  const contract = getLeaseContractForDate(property.lease_contracts, asOf);
  return rent + (contract?.other_monthly_income ?? 0);
};

const getMonthlyExpensesForYield = (property: PropertyHolder, asOf: Dayjs): number => {
  const keys = ["repair_fund_cost", "building_management_cost", "management_cost", "misc_cost"] as const;
  // 経費情報に表示する、対象月に有効な登録月額を使用する。0円更新も反映する。
  // 想定利回りは登録条件で比較する指標のため、予測値 reserve_fund_price_changes は意図的に除外する。
  // 月次収支は予測値・繰越・日割りも扱う別の指標なので、その計算結果にフォールバックしない。
  const registeredExpenses = keys.reduce((sum, key) => {
    const input = getMonthlyCashflowResultEffectiveOn(property.user_input_monthly_cashflow_results ?? [], key, asOf);
    return sum + (input?.[key] ?? 0);
  }, 0);
  const building = property.property.property_category === "一棟物件" ? property.property.building_detail : undefined;
  return registeredExpenses +
    (building?.management_company_commission ?? 0) +
    (building?.shared_utility_expenses ?? 0) +
    (building?.misc_building_cost ?? 0);
};

const isValidPrice = (price?: number | null): price is number => Number.isFinite(price) && price > 0;

const formatYieldRate = (annualIncome: number, acquisitionCost: number): string => {
  const rate = (annualIncome / acquisitionCost) * 100;
  if (!isValidPrice(acquisitionCost) || !Number.isFinite(rate)) return "-";
  return (Math.round(rate * 100) / 100).toLocaleString(undefined, {
    minimumFractionDigits: 2,
  });
};

// 未計算値には%を付けない。負の利回り（例: -1.20）は通常どおり%を付ける。
export const formatYieldRateForDisplay = (rate: string): string => rate === "-" ? "-" : `${rate}%`;

export const getYieldRateByProperty = (property: PropertyHolder, asOf: Dayjs = dayjs()): string => {
  if (!asOf.isValid() || !isValidPrice(property.property.total_price_of_property)) return "-";
  return formatYieldRate(getMonthlyIncomeForYield(property, asOf) * 12, property.property.total_price_of_property);
};

type YieldOptions = {
  // 日付未指定は明示的に今日を採用。月次収支の省略可能な month には依存しない。
  asOf?: Dayjs;
  annualPropertyTax?: number;
};

export const getActualYieldRateByProperty = (
  property: PropertyHolder,
  { asOf = dayjs(), annualPropertyTax = 0 }: YieldOptions = {}
): string => {
  if (!asOf.isValid() || !isValidPrice(property.property.total_price_of_property)) return "-";
  const annualNetIncome =
    (getMonthlyIncomeForYield(property, asOf) - getMonthlyExpensesForYield(property, asOf)) * 12 - annualPropertyTax;
  const acquisitionCost = property.property.total_price_of_property +
    (property.property.property_acquisition_tax ?? 0) + (property.property.misc_expences_at_bought ?? 0);
  return formatYieldRate(annualNetIncome, acquisitionCost);
};

// 紹介物件の月額・価格から表示する利回り（既存の未入力表示・丸めを維持）。
export const calculateYieldRate = (monthlyRent?: number | null, price?: number | null): string => {
  if (!monthlyRent || !Number.isFinite(monthlyRent) || !isValidPrice(price)) return "-";
  return `${((100 * monthlyRent * 12) / price).toFixed(2)}%`;
};
